import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import type {
  ActivityFilter,
  ComputeTypeFilter,
  NetworkFilter,
} from "./useOrganizationComputeResourceList";

export type OwnershipFilter = "all" | "mine";

export interface ComputeListFilters {
  creatorId: string;
  network: NetworkFilter;
  ownership: OwnershipFilter;
  status: ActivityFilter;
  type: ComputeTypeFilter;
}

export type ComputeListFilterUpdate = Partial<ComputeListFilters>;

const getActivityFilter = (value: string | null): ActivityFilter =>
  value === "active" || value === "inactive" ? value : "all";

const getComputeTypeFilter = (value: string | null): ComputeTypeFilter =>
  value === "cloud" || value === "local" ? value : "all";

const getOwnershipFilter = (value: string | null): OwnershipFilter =>
  value === "mine" ? "mine" : "all";

const getNetworkFilter = (value: string | null): NetworkFilter =>
  value === "meshed" || value === "isolated" || value === "egress-only"
    ? value
    : "all";

export function useComputeListFilters() {
  const history = useHistory();
  const location = useLocation();
  const filters = useMemo<ComputeListFilters>(() => {
    const params = new URLSearchParams(location.search);
    return {
      creatorId: params.get("creator") ?? "all",
      network: getNetworkFilter(params.get("network")),
      ownership: getOwnershipFilter(params.get("owner")),
      status: getActivityFilter(params.get("status")),
      type: getComputeTypeFilter(params.get("type")),
    };
  }, [location.search]);

  const setFilters = useCallback(
    (update: ComputeListFilterUpdate) => {
      const nextFilters = { ...filters, ...update };
      const nextParams = new URLSearchParams(location.search);

      if (nextFilters.creatorId === "all") nextParams.delete("creator");
      else nextParams.set("creator", nextFilters.creatorId);

      if (nextFilters.status === "all") nextParams.delete("status");
      else nextParams.set("status", nextFilters.status);

      if (nextFilters.type === "all") nextParams.delete("type");
      else nextParams.set("type", nextFilters.type);

      if (nextFilters.ownership === "all") nextParams.delete("owner");
      else nextParams.set("owner", nextFilters.ownership);

      if (nextFilters.network === "all") nextParams.delete("network");
      else nextParams.set("network", nextFilters.network);

      const nextSearch = nextParams.toString();
      history.push({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      });
    },
    [filters, history, location.pathname, location.search],
  );

  return { filters, setFilters };
}
